import React from "react";
import { MoreHorizontal } from "lucide-react";
import { cn } from "../../lib/utils";
import type { 
  MapWorkspaceCommandBarProps, 
  MapWorkspaceActionItem,
  MapWorkspaceStatusBadge
} from "../../contracts/map-workspace";

/**
 * MapWorkspaceCommandBar
 * 
 * A redesigned, operator-facing command surface for map-centric workbenches.
 * Organizes commands into three functional regions: Context, Workspace Actions, and Commit Actions.
 * Designed for high-density desktop usage with a focus on hierarchy and visual clarity.
 */
export function MapWorkspaceCommandBar({
  mode,
  primaryActions,
  secondaryActions = [],
  overflowActions = [],
  statusBadges = [],
  onAction,
  onOverflowAction,
  className,
}: MapWorkspaceCommandBarProps) {
  
  /**
   * Generates classes for action buttons based on their role (primary/secondary) and state.
   */
  const getActionClasses = (action: MapWorkspaceActionItem, isPrimary: boolean) => {
    const { tone, active, disabled } = action;
    
    const base = cn(
      "relative inline-flex min-w-0 items-center justify-center gap-2 px-2 transition-all select-none shrink-0 md:px-3",
      disabled && "opacity-40 cursor-not-allowed grayscale pointer-events-none shadow-none"
    );

    if (isPrimary) {
      // Primary/Commit Actions: Solid, bold, high emphasis, compact height
      const primaryBase = cn(
        base,
        "h-8 text-[10px] font-black uppercase tracking-tight rounded-[2px] border shadow-sm md:text-[11px]"
      );
      
      switch (tone) {
        case "danger": 
          return cn(primaryBase, "bg-destructive text-destructive-foreground border-destructive/20 hover:bg-destructive/90");
        case "warning": 
          return cn(primaryBase, "bg-amber-500 text-black border-amber-600/20 hover:bg-amber-600");
        case "success": 
          return cn(primaryBase, "bg-emerald-600 text-white border-emerald-700/20 hover:bg-emerald-700");
        default: 
          return cn(primaryBase, "bg-primary text-primary-foreground border-primary/20 hover:bg-primary/90");
      }
    }

    // Secondary/Workspace Actions: Toolbar-style, ghost with bottom indicator if active
    const secondaryBase = cn(
      base,
      "h-full text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
    );

    return cn(
      secondaryBase,
      active && "text-foreground",
      tone === "danger" && "hover:text-destructive hover:bg-destructive/5",
      tone === "warning" && "hover:text-amber-600 hover:bg-amber-500/5",
      tone === "success" && "hover:text-emerald-600 hover:bg-emerald-500/5"
    );
  };

  /**
   * Renders a compact status badge for the context region.
   */
  const renderBadge = (badge: MapWorkspaceStatusBadge) => (
    <div
      key={badge.id}
      className={cn(
        "flex items-center gap-1.5 px-2 h-5 rounded-[2px] text-[9px] font-bold uppercase tracking-tighter border shrink-0 bg-background/50",
        badge.tone === "success" && "text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
        badge.tone === "warning" && "text-amber-600 border-amber-500/20 dark:text-amber-400",
        badge.tone === "danger" && "text-rose-600 border-rose-500/20 dark:text-rose-400",
        (!badge.tone || badge.tone === "default") && "text-muted-foreground border-border",
        badge.tone === "muted" && "text-muted-foreground/50 border-border/50 bg-transparent"
      )}
    >
      <div className={cn(
        "w-1 h-1 rounded-full",
        badge.tone === "success" && "bg-emerald-500",
        badge.tone === "warning" && "bg-amber-500",
        badge.tone === "danger" && "bg-rose-500",
        (!badge.tone || badge.tone === "default") && "bg-slate-400",
        badge.tone === "muted" && "bg-slate-300"
      )} />
      {badge.label}
    </div>
  );

  /**
   * Renders an individual action item.
   */
  const renderAction = (action: MapWorkspaceActionItem, isPrimary: boolean) => (
    <button
      key={action.id}
      onClick={() => !action.disabled && onAction(action.id)}
      disabled={action.disabled}
      className={getActionClasses(action, isPrimary)}
      title={action.description || action.label}
    >
      {action.icon && <span className="w-3.5 h-3.5 shrink-0 opacity-90">{action.icon}</span>}
      <span className="truncate">{action.label}</span>
      {action.badge && (
        <span className="ml-1 px-1 h-3.5 flex items-center rounded-[2px] bg-foreground/10 text-[9px] font-black uppercase">
          {action.badge}
        </span>
      )}
      {!isPrimary && action.active && (
        <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-t-full" />
      )}
    </button>
  );

  return (
    <div
      className={cn(
        "flex h-12 w-full shrink-0 items-center justify-end overflow-hidden border-b border-border bg-background shadow-[0_1px_2px_rgba(0,0,0,0.03)] md:justify-between",
        className
      )}
    >
      {/* 
        Region 1: Context (Left)
        Displays the current mode and session status badges.
        Framed with a subtle background and border to separate context from controls.
      */}
      <div className="hidden h-full min-w-max items-center gap-4 border-r border-border bg-muted/10 pl-4 pr-3 md:flex">
        <div className="flex items-center gap-2">
          <div className={cn(
            "px-2 py-0.5 rounded-[2px] text-[10px] font-black uppercase tracking-widest text-white shadow-xs select-none",
            mode === "author" && "bg-blue-600 dark:bg-blue-500",
            mode === "view" && "bg-slate-500 dark:bg-slate-600",
            mode === "customize-save-copy" && "bg-amber-600 dark:bg-amber-500"
          )}>
            {mode.replace(/-/g, " ")}
          </div>
        </div>

        {statusBadges.length > 0 && (
          <div className="flex items-center gap-1 border-l border-border/50 pl-3 h-6">
            {statusBadges.map(renderBadge)}
          </div>
        )}
      </div>

      {/* 
        Region 2: Workspace Actions (Middle)
        Standard workbench tools and configuration commands.
        Uses a dense toolbar-style layout with active indicators.
      */}
      <div className="hidden h-full flex-1 items-center gap-0.5 overflow-x-auto px-2 no-scrollbar md:flex">
        {secondaryActions.map((action) => renderAction(action, false))}
      </div>

      {/* 
        Region 3: Commit Actions (Right)
        Primary workflow completion actions (Save, Publish, etc.).
        Separated visually to emphasize their terminal role in the session.
      */}
      <div className="flex h-full min-w-0 flex-1 items-center px-0 md:min-w-max md:flex-none md:gap-3 md:border-l md:border-border md:bg-muted/10 md:pl-3 md:pr-4">
        <div className="grid min-w-0 w-full grid-cols-[repeat(auto-fit,minmax(0,1fr))] gap-1 md:flex md:w-auto md:items-center md:gap-2">
          {primaryActions.map((action) => renderAction(action, true))}
        </div>

        {overflowActions.length > 0 && (
          <div className="ml-1 hidden h-6 items-center border-l border-border/50 pl-3 md:flex">
            <button
              onClick={() => onOverflowAction?.("overflow")}
              className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-accent text-muted-foreground transition-colors hover:text-foreground"
              title="More actions"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
